import { ScorecardConfigKPI, ScorecardConfigCategory, ScorecardConfigResponse, ScorecardConfigSubtype, ScorecardConfigType } from "api";

/**
 * Helper function to get units for non-waste categories (from categories>kpis>units)
 * @param dropdownsData - The scorecard configuration data
 * @param categoryName - The selected category name
 * @param selectedKpi - The selected KPI name
 * @returns Array of unit strings
 */
export const getUnitsForNonWasteCategory = (
    dropdownsData: ScorecardConfigResponse | null | undefined,
    categoryName: string,
    selectedKpi: string
): string[] => {
    if (!dropdownsData?.categories) return [];

    const selectedCategory = dropdownsData.categories.find((cat: ScorecardConfigCategory) => cat.categoryName === categoryName);
    if (!selectedCategory?.kpis) return [];

    const selectedKpiData = selectedCategory.kpis.find((kpiItem: ScorecardConfigKPI) => kpiItem.name === selectedKpi);
    return selectedKpiData?.units || [];
};

/**
 * Helper function to get units for waste category (from types>subtypes>units or types>units)
 * @param dropdownsData - The scorecard configuration data
 * @param selectedType - The selected type name
 * @param selectedSubtype - The selected subtype name
 * @returns Array of unit strings
 */
export const getUnitsForWasteCategory = (
    dropdownsData: ScorecardConfigResponse | null | undefined,
    selectedType: string,
    selectedSubtype: string
): string[] => {
    if (!dropdownsData?.types) return [];

    const selectedTypeData = dropdownsData.types.find((type: ScorecardConfigType) => type.type === selectedType);
    if (!selectedTypeData) return [];

    // If subtype is selected and has units, use subtype units
    if (selectedSubtype && selectedTypeData.subtypes) {
        const selectedSubtypeData = selectedTypeData.subtypes.find((subtype: ScorecardConfigSubtype) => subtype.subtype === selectedSubtype);
        if (selectedSubtypeData?.units && selectedSubtypeData.units.length > 0) {
            return selectedSubtypeData.units;
        }
    }

    // Otherwise, use type units
    return selectedTypeData.units || [];
};

/**
 * Get the appropriate units based on category and selections
 * @param dropdownsData - The scorecard configuration data
 * @param category - The selected category
 * @param kpi - The selected KPI (for non-waste categories)
 * @param types - The selected type (for waste categories)
 * @param subtypes - The selected subtype (for waste categories)
 * @returns Array of unit strings
 */
export const getDefaultUnitOptions = (
    dropdownsData: ScorecardConfigResponse | null | undefined,
    category: string,
    kpi: string,
    types: string,
    subtypes: string
): string[] => {
    if (category === 'Waste') {
        return getUnitsForWasteCategory(dropdownsData, types, subtypes);
    }
    return getUnitsForNonWasteCategory(dropdownsData, category, kpi);

};
